import { Loader } from '@googlemaps/js-api-loader'
import { GooglePlace } from '~/models/GPlace'
import Settings from '~/services/settings'
import PlaceEntityMapper from './entityMapper'
import { Place } from '~/models/Place'
import { MapLocation } from '~/services/map/types'
export class GoogleRepository {
	static sessionToken: string
	static loader = new Loader({
		apiKey: Settings.googleMapsApiId(),
		version: 'weekly',
	})

	static async findPlacesPredictions(
		searchTerm: string,
		types: string[] = [],
		language: string
	): Promise<GooglePlace[]> {
		const sessionToken = await this.getSessionToken()

		const { AutocompleteSuggestion } = await this.loader.importLibrary('places')
		const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
			input: searchTerm,
			sessionToken,
			includedPrimaryTypes: types,
			language,
		})

		return suggestions.map((suggestion: google.maps.places.AutocompleteSuggestion) => {
			const placePrediction = suggestion.placePrediction
			return this.parsePrediction(placePrediction!)
		})
	}

	static async nearbyPlacesByText(
		query: string,
		location: MapLocation,
		language?: string
	): Promise<Place[]> {
		const { Place } = await this.loader.importLibrary('places')
		const { center, northEast } = this.toLatLng(location)

		const request: google.maps.places.SearchByTextRequest = {
			textQuery: query,
			fields: this.googleSearchFields,
			locationBias: { center, radius: await this.getVisibleRadius(center, northEast) },
			maxResultCount: 20,
			language,
		}

		const { places } = await Place.searchByText(request)

		return PlaceEntityMapper.toPlaces(places || [])
	}

	static async nearbyPlacesByTypes(
		types: string[],
		location: MapLocation,
		language?: string
	): Promise<Place[]> {
		const { Place, SearchNearbyRankPreference } = await this.loader.importLibrary('places')
		const { center, northEast } = this.toLatLng(location)

		const request = {
			fields: this.googleSearchFields,
			locationRestriction: {
				center,
				radius: (await this.getVisibleRadius(center, northEast)) || 2000,
			},
			includedPrimaryTypes: types,
			maxResultCount: 20,
			rankPreference: SearchNearbyRankPreference.POPULARITY,
			language,
		}

		const { places } = await Place.searchNearby(request)

		return PlaceEntityMapper.toPlaces(places || [])
	}

	static async getPlaceInfo(placeId: string, language?: string): Promise<Place | undefined> {
		const { Place } = await this.loader.importLibrary('places')

		const place: google.maps.places.Place | null = await new Promise((resolve, rejected) => {
			const response = new Place({ id: placeId, requestedLanguage: language })
			response
				.fetchFields({ fields: this.googleSearchFields })
				.then((response: any) => {
					resolve(response.place)
				})
				.catch(() => {
					rejected()
				})
		})
		if (!place) return
		return PlaceEntityMapper.toPlace(place)
	}

	private static parsePrediction(prediction: google.maps.places.PlacePrediction): GooglePlace {
		return {
			placeId: prediction.placeId,
			name: prediction.mainText!.text,
			address: prediction.secondaryText?.text,
		}
	}

	private static async getSessionToken(): Promise<string> {
		if (this.sessionToken) return this.sessionToken

		const { AutocompleteSessionToken } = await this.loader.importLibrary('places')
		this.sessionToken = new AutocompleteSessionToken() as string

		return this.sessionToken
	}

	private static googleSearchFields = [
		'id',
		'displayName',
		// 'websiteURI',
		// 'googleMapsURI',
		// 'nationalPhoneNumber',
		// 'rating',
		'types',
		'photos',
		// 'formattedAddress',
		'location',
	]

	private static toLatLng(location: MapLocation): {
		center: google.maps.LatLng
		northEast?: google.maps.LatLng
	} {
		return {
			center: new google.maps.LatLng(location.center.latitude, location.center.longitude),
			northEast: location.northEast
				? new google.maps.LatLng(location.northEast.latitude, location.northEast.longitude)
				: undefined,
		}
	}

	private static async getVisibleRadius(
		center: google.maps.LatLng,
		northEast?: google.maps.LatLng
	): Promise<number> {
		const GOOGLE_MAX_RADIUS_AVAILABLE = 5000
		if (!northEast) return GOOGLE_MAX_RADIUS_AVAILABLE
		const { spherical } = await this.loader.importLibrary('geometry')
		const radius = spherical.computeDistanceBetween(center, northEast)
		return radius <= GOOGLE_MAX_RADIUS_AVAILABLE ? radius : GOOGLE_MAX_RADIUS_AVAILABLE
	}
}
